import React, { useState } from "react";
import {
  Box,
  TextField,
  Button,
  Stack,
  Typography,
  Chip,
} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import DeleteIcon from "@mui/icons-material/Delete";
import { useTranslation } from "react-i18next";

interface ClientNamesFieldProps {
  label: string;
  clientNames: string[];
  onChange: (clientNames: string[]) => void;
}

export const ClientNamesField: React.FC<ClientNamesFieldProps> = ({
  label,
  clientNames,
  onChange,
}) => {
  const { t } = useTranslation();
  const [newClientName, setNewClientName] = useState("");

  const handleAdd = () => {
    if (newClientName.trim()) {
      onChange([...clientNames, newClientName.trim()]);
      setNewClientName("");
    }
  };

  const handleRemove = (index: number) => {
    onChange(clientNames.filter((_, i) => i !== index));
  };

  const handleKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === "Enter") {
      e.preventDefault();
      handleAdd();
    }
  };

  return (
    <Box>
      <Typography variant="body2" sx={{ mb: 1, fontWeight: 500 }}>
        {label}
      </Typography>

      {/* Input to add new client name */}
      <Stack direction="row" spacing={1} sx={{ mb: 2, alignItems: "flex-start" }}>
        <TextField
          size="small"
          placeholder={t("ENTER_CLIENT_NAME_PLACEHOLDER") || "Enter client name (e.g., client1)"}
          value={newClientName}
          onChange={(e) => setNewClientName(e.target.value)}
          onKeyPress={handleKeyPress}
          sx={{ width: "300px" }}
        />
        <Button
          variant="contained"
          color="primary"
          onClick={handleAdd}
          disabled={!newClientName.trim()}
          startIcon={<AddIcon />}
          sx={{
            textTransform: "none",
            whiteSpace: "nowrap",
            minWidth: "100px",
          }}
        >
          {t("ADD_CLIENT_NAME") || "Add Client"}
        </Button>
      </Stack>

      {/* List of added client names */}
      {clientNames.length > 0 && (
        <Box
          sx={{
            display: "flex",
            flexWrap: "wrap",
            gap: 1,
            p: 2,
            backgroundColor: "#f5f5f5",
            borderRadius: 1,
          }}
        >
          {clientNames.map((name, index) => (
            <Chip
              key={index}
              label={name}
              onDelete={() => handleRemove(index)}
              deleteIcon={<DeleteIcon />}
              sx={{
                backgroundColor: "#fff",
                border: "1px solid #e0e0e0",
              }}
            />
          ))}
        </Box>
      )}

      {clientNames.length === 0 && (
        <Typography
          variant="caption"
          sx={{ color: "text.secondary", fontStyle: "italic" }}
        >
          {t("NO_CLIENT_NAMES_ADDED") || "No client names added yet"}
        </Typography>
      )}
    </Box>
  );
};
